import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Download } from "lucide-react";
import { api, apiError, BACKEND } from "@/lib/api";
import { AdminTitle } from "./AdminLayout";

const LEAD = ["New", "Contacted", "Appointment booked", "Test drive", "Negotiating", "Sold", "Lost"];
const CFG = {
  enquiries: { title: "Enquiries", statuses: LEAD, cols: [["enquiry_type", "Type"], ["vehicle_title", "Vehicle"], ["lead_source", "Source"]] },
  valuations: { title: "Valuation requests", statuses: LEAD, cols: [["registration", "Reg"], ["make", "Make"], ["model", "Model"], ["mileage", "Mileage"]] },
  reservations: { title: "Reservations", statuses: ["Pending", "Confirmed", "Cancelled", "Refunded"], cols: [["vehicle_title", "Vehicle"], ["amount", "Amount £"], ["payment_status", "Payment"]] },
};
const HIDE = new Set(["id", "website", "photos", "part_exchange_details"]);

function toCsv(rows) {
  if (!rows.length) return "";
  const keys = Object.keys(rows[0]).filter((k) => !HIDE.has(k));
  const esc = (x) => `"${String(typeof x === "object" && x !== null ? JSON.stringify(x) : x ?? "").replace(/"/g, '""')}"`;
  return [keys.join(","), ...rows.map((r) => keys.map((k) => esc(r[k])).join(","))].join("\n");
}

export default function AdminLeads({ kind }) {
  const cfg = CFG[kind];
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const [filter, setFilter] = useState("");
  const load = () => api.get(`/admin/${kind}`).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { setRows(null); setOpen(null); load(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (r, status) => {
    try { await api.patch(`/admin/${kind}/${r.id}`, { status }); toast.success(status === "Confirmed" ? "Reservation confirmed — vehicle marked Reserved" : "Status updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const exportCsv = () => {
    const blob = new Blob([toCsv(rows || [])], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${kind}.csv`; a.click();
  };
  const shown = (rows || []).filter((r) => !filter || r.status === filter);

  return (
    <div data-testid={`admin-${kind}`}>
      <AdminTitle title={cfg.title}>
        <select className="field !w-48 !py-2.5 text-sm" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid={`${kind}-filter`}>
          <option value="">All statuses</option>{cfg.statuses.map((s) => <option key={s}>{s}</option>)}
        </select>
        <button onClick={exportCsv} className="btn btn-secondary !py-3" data-testid={`${kind}-export`}><Download className="h-4 w-4" />Export CSV</button>
      </AdminTitle>
      {kind === "reservations" && <p className="mb-4 text-sm text-charcoal/75">Payments are not connected. Setting a reservation to <strong>Confirmed</strong> marks the vehicle as Reserved.</p>}
      <div className="overflow-x-auto border border-stone bg-paper">
        <table className="w-full min-w-[860px] text-left text-sm">
          <thead className="border-b border-stone text-xs uppercase tracking-[0.12em] text-charcoal/70">
            <tr><th className="p-3">Date</th><th className="p-3">Name</th><th className="p-3">Contact</th>{cfg.cols.map(([, l]) => <th key={l} className="p-3">{l}</th>)}<th className="p-3">Status</th><th /></tr>
          </thead>
          <tbody>
            {rows === null && <tr><td className="p-4" colSpan={9}>Loading…</td></tr>}
            {rows && shown.length === 0 && <tr><td className="p-4 text-charcoal/70" colSpan={9}>Nothing here yet.</td></tr>}
            {shown.map((r) => (
              <Fragment key={r.id}>
                <tr className="border-b border-stone/60" data-testid={`${kind}-row`}>
                  <td className="p-3 whitespace-nowrap">{new Date(r.created_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}</td>
                  <td className="p-3">{r.name}{r.part_exchange && <span className="ml-2 bg-stone px-1.5 py-0.5 text-[0.65rem] uppercase">PX</span>}</td>
                  <td className="p-3 text-xs"><a href={`mailto:${r.email}`} className="block underline">{r.email}</a><a href={`tel:${r.phone}`}>{r.phone}</a></td>
                  {cfg.cols.map(([k]) => <td key={k} className="p-3">{String(r[k] ?? "—") || "—"}</td>)}
                  <td className="p-3">
                    <select className="field !w-44 !py-1.5 text-xs" value={r.status} onChange={(e) => setStatus(r, e.target.value)} data-testid={`${kind}-status`}>
                      {cfg.statuses.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="p-3"><button onClick={() => setOpen(open === r.id ? null : r.id)} aria-label="Details"><ChevronDown className={`h-4 w-4 transition-transform ${open === r.id ? "rotate-180" : ""}`} /></button></td>
                </tr>
                {open === r.id && (
                  <tr className="border-b border-stone bg-[#FBF9F4]"><td colSpan={9} className="p-5">
                    <dl className="grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
                      {Object.entries(r).filter(([k, val]) => !HIDE.has(k) && val !== "" && val !== null && typeof val !== "object").map(([k, val]) => (
                        <div key={k}><dt className="text-xs uppercase tracking-[0.1em] text-charcoal/60">{k.replace(/_/g, " ")}</dt><dd className="whitespace-pre-line">{String(val)}</dd></div>
                      ))}
                      {Object.entries(r.part_exchange_details || {}).map(([k, val]) => <div key={k}><dt className="text-xs uppercase tracking-[0.1em] text-charcoal/60">PX {k.replace(/_/g, " ")}</dt><dd>{val || "—"}</dd></div>)}
                    </dl>
                    {r.photos && Object.keys(r.photos).length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-3">
                        {Object.entries(r.photos).flatMap(([slot, paths]) => paths.map((p) => (
                          <a key={p} href={`${BACKEND}/api/files/${p}`} target="_blank" rel="noreferrer" className="text-center text-xs">
                            <img src={`${BACKEND}/api/files/${p}`} alt={slot} className="h-20 w-28 object-cover" /><span>{slot}</span>
                          </a>
                        )))}
                      </div>
                    )}
                  </td></tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
